import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, firstValueFrom, Observable, Subject } from 'rxjs';
import { MercureService } from '@artemis-software/wr-api';
import { AuthService } from '@services/auth.service';

export interface BaseEvent<TType> {
  type: TType;
  date: string;
}

export interface InspectionImageZipReadyEvent extends BaseEvent<'InspectionImageZipReady'> {
  downloadUrl: string;
  inspectionId: string;
  skippedCount: number;
}

export interface InspectionImageZipFailedEvent extends BaseEvent<'InspectionImageZipFailed'> {
  inspectionId: string;
  reason: string;
}

export type MercureEvent = NotificationCreatedEvent | InspectionImageZipReadyEvent | InspectionImageZipFailedEvent;

export type MercureEventType = MercureEvent['type'];

export type UnwrapEventType<T> = T extends MercureEventType
  ? Extract<MercureEvent, { type: T }>
  : never;

export interface NotificationCreatedEvent extends BaseEvent<'NotificationCreated'> {
  notificationId: string;
}

@Injectable()
export class ServerSentEventsService {
  eventSource?: EventSource;

  private readonly mercureService = inject(MercureService);
  private readonly authService = inject(AuthService);
  private readonly events$ = new Subject<MercureEvent>();
  private initializing = false;

  constructor() {
    // Das Hub-Token im EventSource-URL laeuft erst nach 24h ab und wird vom Hub nicht gegen
    // die WR-Revokation geprueft. Ohne close() liefe der Stream nach dem Logout auf den
    // Topics des abgemeldeten Benutzers weiter, bis der Tab geschlossen wird.
    this.authService.loggedOut$.pipe(takeUntilDestroyed()).subscribe(() => this.closeEventSource());
    this.authService.loggedIn$.pipe(takeUntilDestroyed()).subscribe(() => void this.initEventSource());
  }

  private closeEventSource(): void {
    this.eventSource?.close();
    this.eventSource = undefined;
  }

  private async initEventSource(): Promise<void> {
    // Der Guard vor dem await reicht allein nicht: fallen Logout und erneuter Login in die
    // Laufzeit von getMercureUrl(), liefen zwei Aufrufe parallel und der erste EventSource
    // bliebe offen liegen.
    if (this.eventSource || this.initializing) return;
    // Wer sein Passwort wechseln muss, bekommt vom MustChangePasswordFilter auf jeden anderen
    // Pfad 403 - auch hier. Der AuthInterceptor meldet ihn daraufhin wieder ab, und weil dieser
    // Service den Aufruf an jeden Login haengt, käme er nie auf die Force-Change-Seite.
    if (this.authService.authCredentials()?.mustChangePassword) return;

    this.initializing = true;
    try {
      const { url } = await firstValueFrom(this.mercureService.getMercureUrl());
      if (!this.authService.loggedIn() || this.eventSource) return;

      this.eventSource = new EventSource(url);
      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.events$.next(data);
        } catch (e) {
          console.error(e);
          return;
        }
      };
    } catch (e) {
      // Der Stream ist Beiwerk - ohne Fehlerzweig schlaegt jeder fehlgeschlagene Abruf als
      // unbehandelte Promise-Rejection in Sentry auf. Der Toast kam schon vom ErrorInterceptor.
      console.error(e);
    } finally {
      this.initializing = false;
    }
  }

  get all(): Observable<MercureEvent> {
    return this.events$.asObservable();
  }

  event<TType extends MercureEventType>(type: TType): Observable<UnwrapEventType<TType>> {
    return this.events$.pipe(filter((e) => e.type === type)) as Observable<UnwrapEventType<TType>>;
  }

  events<TType extends MercureEventType>(...types: TType[]): Observable<UnwrapEventType<TType>> {
    return this.events$.pipe(
      filter((e) => types.includes(e.type as unknown as TType)),
    ) as Observable<UnwrapEventType<TType>>;
  }
}
